import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { replayService } from '@/services';
import { toast } from 'sonner';
import type { ReplayBundleV1, SessionTemplate, PublicAssessment } from '@arrowise/shared';
import sessionService from '@/services/sessionService';
import { showTemplateDialog } from '@/components/sessions/showTemplateDialog';
import assessmentService from '@/services/assessmentService';
import { useAuth } from '@/providers/AuthProvider';
import ReplayControls from '@/components/replay/ReplayControls';
import ReplayContent from '@/components/replay/ReplayContent';
import AssessmentTab from '@/components/assessments/AssessmentTab';
import AssessmentFormDialog from '@/components/assessments/AssessmentFormDialog';

type Speed = 1 | 2 | 5 | 10;

export default function ViewSessionPage({ nav = true, footer = true }: { nav?: boolean; footer?: boolean }) {
    const { id: sessionId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { user } = useAuth();

    // Check if we came from compare page (via query params or state)
    const fromCompare = searchParams.get('from') === 'compare' || location.state?.from === 'compare';
    const positionId = searchParams.get('positionId') || location.state?.positionId;

    // Get current tab from query param (will be validated after bundle loads)
    const isFreeUser = user?.billingInfo?.subscriptionTier === 'free';
    const currentTab = searchParams.get('tab');

    const [bundle, setBundle] = useState<ReplayBundleV1 | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [t, setT] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [speed, setSpeed] = useState<Speed>(5);
    // Track selection by stable identity across renames
    const [selectedIdentity, setSelectedIdentity] = useState<string | undefined>(undefined);
    const rafRef = useRef<number | null>(null);
    const lastTickRef = useRef<number | null>(null);
    const [template, setTemplate] = useState<SessionTemplate | null>(null);
    const [assessments, setAssessments] = useState<PublicAssessment[]>([]);
    const [loadingAssessments, setLoadingAssessments] = useState(false);
    const [gradingStatus, setGradingStatus] = useState<{ graded: boolean; canGrade: boolean; hasCompleteAssessment: boolean } | null>(null);
    const [loadingGrading, setLoadingGrading] = useState(false);
    const [gradingError, setGradingError] = useState<string | null>(null);
    const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
    const [existingAssessmentForDialog, setExistingAssessmentForDialog] = useState<PublicAssessment | null>(null);
    const [loadingAssessmentDialog, setLoadingAssessmentDialog] = useState(false);

    // Determine if user should have access to replay tab
    // Free users can access replay if they're part of a team that owns the session
    // Paid users always have access to replay
    const canAccessReplay = true;

    // Determine the default tab and current tab
    const defaultTab = canAccessReplay ? 'replay' : 'assessments';
    const actualCurrentTab = currentTab || defaultTab;

    // Handle tab change
    const handleTabChange = (value: string) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', value);
        setSearchParams(newParams);
    };

    // Handle opening assessment dialog
    const handleOpenAssessmentDialog = async () => {
        if (!sessionId || !user) return;

        setLoadingAssessmentDialog(true);
        try {
            // Try to get the user's existing assessment
            const assessment = await assessmentService.getAssessment(sessionId, user.id);
            setExistingAssessmentForDialog(assessment);
        } catch (error: any) {
            // If assessment doesn't exist (404), set to null
            if (error.status === 404) {
                console.log('No existing assessment found, opening empty form');
                setExistingAssessmentForDialog(null);
            } else {
                // For other errors, show error and don't open dialog
                console.error('Failed to fetch assessment:', error);
                toast.error(error.message || 'Failed to load assessment');
                setLoadingAssessmentDialog(false);
                return;
            }
        } finally {
            setLoadingAssessmentDialog(false);
        }

        setAssessmentDialogOpen(true);
    };

    // Load assessments for the session
    const loadAssessments = async () => {
        if (!sessionId || !user) return;
        setLoadingAssessments(true);
        try {
            const data = await assessmentService.getAssessmentsForSession(sessionId);
            setAssessments(data);
        } catch (error) {
            console.error('Failed to load assessments:', error);
        } finally {
            setLoadingAssessments(false);
        }
    };

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        async function poll() {
            if (!sessionId) return;
            try {
                const s = await replayService.getReplayStatus(sessionId);
                if (cancelled) return;
                if (s.ready) {
                    const b = await replayService.getReplayBundle(sessionId);
                    if (cancelled) return;
                    setBundle(b);
                    // pick first visible file at t=0
                    const initialVisible = (b.files.catalog || []).find(f => f.createdAtMs <= 0 && (!f.deletedAtMs || f.deletedAtMs > 0));
                    const initIdentity = initialVisible ? (initialVisible.identity || initialVisible.id || (initialVisible as any).clientFileId || initialVisible.filename) : undefined;
                    setSelectedIdentity(initIdentity);
                    const template = await sessionService.getTemplateById(b.session.templateId, b.session.owner.teamId);
                    setTemplate(template);
                    setLoading(false);
                } else if (s.error) {
                    setError(s.error);
                    setLoading(false);
                } else {
                    setTimeout(poll, 2000);
                }
            } catch (err: any) {
                if (cancelled) return;
                setError(err?.message || 'Failed to load replay');
                setLoading(false);
            }
        }
        setLoading(true);
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    // Load assessments only when on assessments tab
    useEffect(() => {
        if (actualCurrentTab === 'assessments' && sessionId && user) {
            loadAssessments();
        }
    }, [actualCurrentTab, sessionId, user]);

    // Load grading status
    useEffect(() => {
        if (!sessionId || !user) return;
        const loadGradingStatus = async () => {
            try {
                const status = await assessmentService.getSessionGradingStatus(sessionId);
                setGradingStatus(status);
            } catch (error) {
                console.error('Failed to load grading status:', error);
            }
        };
        loadGradingStatus();
    }, [sessionId, user]);

    const handleGrade = async () => {
        if (!gradingStatus || !sessionId) return;
        setLoadingGrading(true);
        setGradingError(null);
        try {
            await assessmentService.setSessionGraded(sessionId, !gradingStatus.graded);
            // Reload status
            const status = await assessmentService.getSessionGradingStatus(sessionId);
            setGradingStatus(status);
            // Reload assessments if on assessments tab
            if (actualCurrentTab === 'assessments') {
                loadAssessments();
            }
            toast.success('Assessment status updated successfully');
        } catch (error: any) {
            setGradingError(error.message || 'Failed to update assessment status');
        } finally {
            setLoadingGrading(false);
        }
    };

    const effectiveT = useMemo(() => {
        if (!bundle) return t;
        return t;
    }, [bundle, t]);

    // Visible files based on current time (replay mode)
    const visibleFiles = useMemo(() => {
        if (!bundle) return [] as Array<{ identity: string; displayName: string; createdAtMs: number; deletedAtMs?: number }>;
        const list: Array<{ identity: string; displayName: string; createdAtMs: number; deletedAtMs?: number }> = [];
        for (const f of bundle.files.catalog || []) {
            const active = f.createdAtMs <= effectiveT && (!f.deletedAtMs || f.deletedAtMs > effectiveT);
            if (!active) continue;
            const ext = f as any;
            let displayName = f.filename as string;
            if (ext.names && Array.isArray(ext.names) && ext.names.length > 0) {
                const win = ext.names.find((n: any) => n && n.startMs <= effectiveT && (typeof n.endMs === 'undefined' || Number(n.endMs) > effectiveT));
                if (win && win.filename) displayName = String(win.filename);
            }
            const identity = (ext.identity || f.id || ext.clientFileId || f.filename) as string;
            list.push({ identity, displayName, createdAtMs: f.createdAtMs, deletedAtMs: f.deletedAtMs });
        }
        return list;
    }, [bundle, effectiveT]);

    // Ensure selected file is visible; if not, switch to first visible or clear
    useEffect(() => {
        if (!bundle) return;
        if (selectedIdentity && !visibleFiles.some(f => f.identity === selectedIdentity)) {
            setSelectedIdentity(visibleFiles[0]?.identity);
        }
        if (!selectedIdentity && visibleFiles.length > 0) {
            setSelectedIdentity(visibleFiles[0].identity);
        }
    }, [bundle, t, selectedIdentity, visibleFiles]);

    useEffect(() => {
        if (!bundle || !isPlaying) return;
        const step = (now: number) => {
            if (!lastTickRef.current) lastTickRef.current = now;
            const dt = now - lastTickRef.current;
            lastTickRef.current = now;
            setT((prev) => {
                const next = prev + dt * speed;
                const maxT = bundle.session.durationMs;
                if (next >= maxT) {
                    setIsPlaying(false);
                    return maxT;
                }
                return next;
            });
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); lastTickRef.current = null; };
    }, [bundle, isPlaying, speed]);

    const currentStep = useMemo(() => {
        if (!bundle) return undefined;
        return bundle.steps.find(s => effectiveT >= s.startMs && effectiveT < s.endMs) || bundle.steps[bundle.steps.length - 1];
    }, [bundle, effectiveT]);

    const currentFileContent = useMemo(() => {
        if (!bundle || !selectedIdentity) return '';
        // find catalog entry by identity
        const entry = (bundle.files.catalog || []).find(f => {
            const ext = f as any;
            const id = (ext.identity || f.id || ext.clientFileId || f.filename) as string;
            return id === selectedIdentity;
        });
        if (!entry) return '';
        // Build list of names relevant up to time t
        const nameSet = new Set<string>();
        const ext = entry as any;
        if (ext.names && Array.isArray(ext.names) && ext.names.length > 0) {
            for (const n of ext.names) {
                if (n && n.startMs <= effectiveT && n.filename) nameSet.add(String(n.filename));
            }
        }
        nameSet.add(entry.filename);
        let bestT = -1;
        let content = '';
        for (const name of nameSet) {
            const idxs = bundle.indices.byFile[name] || [];
            for (let i = 0; i < idxs.length; i++) {
                const idx = idxs[i];
                const evt = bundle.timeline[idx] as any;
                if (evt.type === 'code_change' && evt.filename === name && evt.t <= effectiveT) {
                    if (evt.t >= bestT) {
                        bestT = evt.t;
                        content = evt.content as string;
                    }
                }
            }
        }
        return content;
    }, [bundle, selectedIdentity, effectiveT]);

    const significantEvents = bundle?.indices.significantEvents || [];
    const currentEventIndex = useMemo(() => {
        if (!bundle) return -1;
        let low = 0, high = significantEvents.length - 1, ans = -1;
        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const evt = bundle.timeline[significantEvents[mid]] as any;
            if (evt.t <= t) { ans = mid; low = mid + 1; } else { high = mid - 1; }
        }
        return ans;
    }, [bundle, t]);

    // Map a timeline filename at time tt to a catalog identity
    function identityForFilenameAtTime(filename: string, tt: number): string | undefined {
        if (!bundle) return undefined;
        for (const f of bundle.files.catalog || []) {
            const ext = f as any;
            if (ext.names && Array.isArray(ext.names) && ext.names.length > 0) {
                const win = ext.names.find((n: any) => n && n.filename === filename && n.startMs <= tt && (typeof n.endMs === 'undefined' || Number(n.endMs) > tt));
                if (win) return (ext.identity || f.id || ext.clientFileId || f.filename) as string;
            }
            if (f.filename === filename && f.createdAtMs <= tt && (!f.deletedAtMs || f.deletedAtMs > tt)) {
                return ((f as any).identity || f.id || (f as any).clientFileId || f.filename) as string;
            }
        }
        return undefined;
    }

    // Auto-focus file being edited (latest code_change at or before current time)
    useEffect(() => {
        if (!bundle) return;
        let idx = currentEventIndex;
        while (idx >= 0) {
            const evt = bundle.timeline[significantEvents[idx]] as any;
            if (evt.type === 'code_change') {
                const identity = identityForFilenameAtTime(evt.filename, evt.t);
                // Only focus if that identity is currently visible at time t to avoid loops on deleted files
                const isVisibleNow = !!identity && visibleFiles.some(f => f.identity === identity);
                if (identity && isVisibleNow && identity !== selectedIdentity) {
                    setSelectedIdentity(identity);
                }
                break;
            }
            idx--;
        }
    }, [bundle, currentEventIndex, selectedIdentity, visibleFiles]);

    function formatMs(ms: number): string {
        const total = Math.floor(ms / 1000);
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
    }

    if (loading) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-7xl w-full space-y-6">
                    <Skeleton className="h-10" />
                    <Skeleton className="h-12" />
                    <div className="grid grid-cols-12 gap-4">
                        <Skeleton className="h-[60vh] col-span-3" />
                        <Skeleton className="h-[60vh] col-span-6" />
                        <Skeleton className="h-[60vh] col-span-3" />
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (error) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-4xl w-full space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Replay not available</CardTitle>
                            <CardDescription>{error}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Button onClick={() => navigate(-1)}>Go back</Button>
                        </CardContent>
                    </Card>
                </div>
            </PageLayout>
        );
    }

    if (!bundle && canAccessReplay) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-4xl w-full space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Preparing replay…</CardTitle>
                            <CardDescription>We're building the session replay. This may take a few moments.</CardDescription>
                        </CardHeader>
                    </Card>
                </div>
            </PageLayout>
        );
    }

    if (!bundle && !isFreeUser) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-4xl w-full space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Preparing replay…</CardTitle>
                            <CardDescription>We’re building the session replay. This may take a few moments.</CardDescription>
                        </CardHeader>
                    </Card>
                </div>
            </PageLayout>
        );
    }

    const duration = bundle?.session.durationMs || 0;

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-6 mx-auto max-w-[1400px] w-full space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Button variant="outline" size="sm" asChild>
                            <Link to={fromCompare && positionId ? `/app/positions/${positionId}/compare` : `/app/sessions`}>
                                <ArrowLeft />
                                {fromCompare ? 'Back to position compare' : 'Back to sessions'}
                            </Link>
                        </Button>
                        <div className="space-y-1">
                            <h1 className="text-2xl font-bold tracking-tight">{canAccessReplay ? 'Session Replay' : 'Session View'}</h1>
                            {bundle && <p className="text-sm text-muted-foreground">{bundle.session.title || 'Untitled'} • {formatMs(t)} / {formatMs(duration)}</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {bundle && (
                            <>
                                <Badge variant="secondary">Tokens: {bundle.ai.totals.totalTokens}</Badge>
                                <Badge variant="outline">Steps: {bundle.steps.length} / {template?.steps?.length} completed</Badge>
                            </>
                        )}
                        {template && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => showTemplateDialog(template)}
                                className="h-6 px-2 text-xs"
                            >
                                View Template
                            </Button>
                        )}
                        {gradingStatus && (
                            <Popover open={!!gradingError} onOpenChange={() => setGradingError(null)}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant={gradingStatus.graded ? "default" : "outline"}
                                        size="sm"
                                        onClick={handleGrade}
                                        disabled={loadingGrading}
                                        className={`h-6 px-2 text-xs ${gradingStatus.graded
                                            ? 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                                            : ''
                                            }`}
                                    >
                                        {loadingGrading && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                                        {gradingStatus.graded ? 'Unmark as \'Assessment Complete\'' : 'Mark as \'Assessment Complete\''}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="center" className="w-64">
                                    <p className="text-sm">{gradingError}</p>
                                </PopoverContent>
                            </Popover>
                        )}
                    </div>
                </div>

                <Tabs value={actualCurrentTab} onValueChange={handleTabChange} className="w-full">
                    <TabsList className={`grid w-full ${canAccessReplay ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        {canAccessReplay && <TabsTrigger value="replay">Session Replay</TabsTrigger>}
                        <TabsTrigger value="assessments">Assessments</TabsTrigger>
                    </TabsList>

                    <TabsContent value="replay" className="space-y-4">
                        {bundle && (
                            <>
                                <ReplayControls
                                    bundle={bundle}
                                    t={t}
                                    setT={setT}
                                    isPlaying={isPlaying}
                                    setIsPlaying={setIsPlaying}
                                    speed={speed}
                                    setSpeed={setSpeed}
                                    currentStep={currentStep}
                                    gradingStatus={gradingStatus}
                                    onOpenAssessmentDialog={handleOpenAssessmentDialog}
                                    loadingAssessment={loadingAssessmentDialog}
                                />
                                <ReplayContent
                                    bundle={bundle}
                                    visibleFiles={visibleFiles}
                                    selectedIdentity={selectedIdentity}
                                    onSelectFile={setSelectedIdentity}
                                    currentFileContent={currentFileContent}
                                    effectiveT={effectiveT}
                                    t={t}
                                    onSeekToTime={setT}
                                />
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="assessments" className="space-y-4">
                        <AssessmentTab
                            sessionId={sessionId!}
                            teamId={bundle?.session.owner.teamId}
                            assessments={assessments}
                            loadingAssessments={loadingAssessments}
                            onLoadAssessments={loadAssessments}
                            isGraded={gradingStatus?.graded || false}
                        />
                    </TabsContent>
                </Tabs>

                {/* Assessment Form Dialog */}
                <AssessmentFormDialog
                    open={assessmentDialogOpen}
                    onOpenChange={(open) => {
                        setAssessmentDialogOpen(open);
                        if (!open) {
                            setExistingAssessmentForDialog(null);
                        }
                    }}
                    sessionId={sessionId!}
                    existingAssessment={existingAssessmentForDialog}
                    onSuccess={() => {
                        loadAssessments();
                        setExistingAssessmentForDialog(null);
                    }}
                />
            </div>
        </PageLayout>
    );
}
